import { I18n } from 'react-redux-i18n';
import {
  GVTabId,
  useGvFilters,
  usePruneRefs,
  useGvPriceWine,
  useGvTab
} from '../hooks.persistence';
import {
  buildSelectedRefQuery,
  getMarketsMissingSurvey,
  getPremiumSurveyYears
} from '../gvScope';
import { useGvCompetitorScope } from '../useGvCompetitorScope';
import { ProductControl } from './GlobalView/ProductControl';
import { AppliedFilters } from './GlobalView/AppliedFilters';
import { MarketControl } from './GlobalView/MarketControl';
import { SurveyDateControl } from './GlobalView/SurveyDateControl';
import { useUserMe } from 'apiTyped/hooks/users.query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import {
  AppliedChip,
  FiltersBadge,
  FilterIcon,
  ResetBtn,
  ChipDropdown,
  MultiChipDropdown,
  Section,
  Label,
  Wrapper as ContextBarWrapper,
  ScopeSummary,
  ContextToggleSection,
  ContentToggleSection
} from '../components/V2ContextBar';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { useTableSort } from '../components/SortableTable';
import {
  ColumnDisplayModal,
  ColumnDef
} from '../components/ColumnDisplayModal';
import { PosFilterModal } from '../components/PosFilterModal';
import {
  PosFilterState,
  DEFAULT_FILTERS,
  countActiveFilters,
  toNumericIds,
  filtersForChannel,
  getMarketSelectionContext,
  getMarketsForContext,
  getSurveyYearForContext,
  setSurveyYearForContext,
  setMarketsForContext,
  resetProductTypeFields,
  buildProductTypeQueryFields,
  buildSpiritAwardQueryFields
} from '../components/posFilterTypes';
import { useV2Filters } from '../context/V2FiltersContext';
import { useDashboardData } from 'apiTyped/hooks/dashboard.query';
import {
  useV2GlobalViewWines,
  useDistributionGlobalDataFilters,
  DistributionGlobalDataFilter
} from 'apiTyped/hooks/global_data_view.query';
import {
  useV2DistributionPos,
  useV2DistributionGeoFilters,
  useV2DistributionPosCount,
  useV2DistributionPosStatusSummary,
  useV2DistributionRaw,
  type V2DistributionPosEvolutionPeriod,
  type V2DistributionPosFilter,
  type V2DistributionRawFilter
} from 'apiTyped/hooks/v2.distribution.query';
import {
  useCrmPipeline,
  useCreateActionList,
  toCreateActionListBody
} from 'apiTyped/hooks/v2.crm.query';
import { buildGlobalViewScope } from './GlobalView.scope';
import { downloadGdvExcel, type GdvExportTab } from '../utils/gdvExport';
import { GlobalLoadingState } from './GlobalView/loading';
import { useScopeRefreshing } from '../utils/useScopeRefreshing';
import { downloadCsv } from '../utils/downloadCsv';
import {
  WriteEmailModal,
  SaveActionListModal
} from './CrmSalesTracking/CrmModals';
import {
  usePriceByVintage,
  useVintageDistribution
} from 'apiTyped/hooks/v2.global_view.query';
import {
  buildPriceByVintageParams,
  getPreviousVintagePoint,
  isVintageWithinRange,
  PriceCompareMode,
  sortVintages
} from './GlobalView.priceByVintage';
import Icon from '../../components/Icon';
import type { PosEntry, PosStatus } from './CrmSalesTracking/types';
import { MarketPositioning } from './MarketPositioning';
import {
  EmptySavedViews,
  GVActionRow,
  GVBar,
  GVControlColumn,
  GVFiltersBtn,
  GVPrimaryRow,
  GVScopeControls,
  PeriodToggle,
  RemoveViewBtn,
  SaveViewInput,
  SaveViewTriggerBtn,
  SavedViewInfo,
  SavedViewRow,
  SavedViewsBtn,
  SavedViewsCloseBtn,
  SavedViewsList,
  SavedViewsOverlay,
  SavedViewsPopHead,
  SavedViewsPopTitle,
  SavedViewsPopover,
  SavedViewsSearchInput
} from './GlobalView/GlobalViewFilters.styles';
import { KEY_COLUMNS } from './GlobalView/KeyIndicatorsTab';
import { GV_POS_COLUMNS } from './GlobalView/PointsOfSaleTab';
import { getRawColumns } from './GlobalView/RawDataTab';
import {
  GV_ROW_LIMIT_OPTIONS,
  GroupBy,
  NoData,
  PosStatusChip,
  Td,
  TdNum,
  countRefs,
  formatRawWineFormat,
  ToastBox
} from './GlobalView/shared';
import { KeyIndicatorsTab } from './GlobalView/KeyIndicatorsTab';
import { PointsOfSaleTab } from './GlobalView/PointsOfSaleTab';
import { VintagePriceTab } from './GlobalView/VintagePriceTab';
import { RawDataTab } from './GlobalView/RawDataTab';
import { PosTrackingDrawer } from './GlobalView/PosTrackingDrawer';

type CompareMode = 'none' | 'prev' | 'competitor';

const PAGE_SIZE = 25;

const SAVED_VIEWS_KEY = 'ws_gv_saved_views_v1';

const formatSurveyDate = (isoDate?: string | null): string => {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getUTCFullYear()}`;
};

interface SavedView {
  id: string;
  name: string;
  createdAt: string;
  filters: PosFilterState;
  productType: '1' | '2';
  comparisonPeriod?: V2DistributionPosEvolutionPeriod;
}

function loadSavedViews(): SavedView[] {
  try {
    const raw = window.localStorage.getItem(SAVED_VIEWS_KEY);
    if (!raw) return [];
    const views = JSON.parse(raw) as SavedView[];
    return views.map((view) => ({
      ...view,
      comparisonPeriod: 'latest',
      productType: view.productType === '2' ? '2' : '1',
      filters: {
        ...DEFAULT_FILTERS,
        ...view.filters,
        spiritCategory: view.filters.spiritCategory ?? 'all',
        spiritCountry: view.filters.spiritCountry ?? 'all'
      }
    }));
  } catch {
    return [];
  }
}

function persistSavedViews(views: SavedView[]) {
  try {
    window.localStorage.setItem(SAVED_VIEWS_KEY, JSON.stringify(views));
  } catch {
    /* localStorage unavailable — ignore */
  }
}

const statusFilterFromChip = (
  status: PosStatusChip
): V2DistributionPosFilter['pos_status'] => {
  if (status === 'active') return 'pos';
  if (status === 'towin') return 'conquer';
  if (status === 'lost') return 'lost';
  if (status === 'other') return 'other';
  return null;
};

const drawerStatus = (status?: string | null): PosStatus => {
  if (status === 'active' || status === 'lost' || status === 'prospect') {
    return status;
  }
  return 'prospect';
};

/**
 * Adapts a `/v2/distribution/pos` row into the `PosEntry` shape expected by
 * the shared `PosDrawer`, so clicking a row here opens proto's exact same
 * canonical POS Detail Drawer (`openDrawer()` / `WS_openPOSDrawer`) that the
 * Points of Sale page itself uses — not a re-implementation.
 */
function rowToPosEntry(row: any): PosEntry {
  const id = row.entity_id ?? 0;
  const clientCount = countRefs(row.client_refs);
  const compCount = countRefs(row.rival_refs);
  const totalRefs = row.facing ?? clientCount + compCount;
  return {
    id,
    name: row.entity_name || '—',
    type: '—',
    city: row.store_city_name || '—',
    region: row.market_name || '—',
    country: row.market_name || '—',
    status: drawerStatus(row.pos_status),
    network: row.network === 'off' ? 'off' : 'on',
    yourRef: clientCount,
    totalRefs,
    competitors: compCount,
    lastChange: '—',
    evolution: 0,
    address: [row.store_city_name, row.market_name].filter(Boolean).join(', '),
    wineListSize: totalRefs,
    avgPrice:
      row.av_price != null
        ? `${Number(row.av_price).toFixed(0)} ${
            row.displayed_currency || 'USD'
          }`
        : '—',
    lastChecked: '—',
    shelves: clientCount
  };
}

const defaultVisibility = (cols: ColumnDef[]): Record<string, boolean> =>
  cols.reduce(
    (acc, c) => ({ ...acc, [c.id]: c.defaultOn }),
    {} as Record<string, boolean>
  );

const Page = styled.div`
  max-width: 1400px;
`;

// Wine/Spirits category toggle (proto's #gvCategoryToggle).
const CategoryToggle = styled.div`
  display: inline-flex;
  align-items: center;
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 3px;
  margin-bottom: 6px;
`;

const CategoryToggleBtn = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? 'var(--ink)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink-faint)')};
  border: none;
  border-radius: 999px;
  padding: 5px 14px;
  font-family: var(--font-display);
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s;

  &:not(:disabled):hover {
    color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink)')};
  }
`;

const SurveyCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const SurveyHeading = styled.div`
  font-family: var(--font-serif, var(--font-display));
  font-size: 15px;
  font-weight: 300;
  color: var(--terracotta, #c84b1f);
`;

const SurveyStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const SurveyCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 4px;
  margin: -4px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--paper, #f9f7f4);
  }
`;

const SurveyThumb = styled.div<{ $bg: string }>`
  position: relative;
  width: 84px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 8px;
  background-image: ${({ $bg }) => ($bg ? `url('${$bg}')` : 'none')};
  background-size: cover;
  background-position: center;
  background-color: var(--paper-warm, #f2ede4);
`;

const SurveyStamp = styled.span`
  position: absolute;
  inset: 8px auto auto 8px;
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border: 1.5px dashed var(--white, #fff);
  border-radius: 50%;
  color: var(--white, #fff);
  font-size: 12px;
  font-weight: 700;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.35));
`;

const SurveyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
`;

const SurveyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SurveyName = styled.span`
  font-family: var(--font-body);
  font-size: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--ink);
  white-space: nowrap;
`;

const SurveyBadge = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--paper, #f9f7f4);
  color: var(--ink-muted, #6b675e);
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SurveyDate = styled.div`
  font-family: var(--font-body);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--terracotta, #c84b1f);
`;

const CompetitorSection = styled(Section)`
  position: relative;
`;

const TabSection = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;
`;

const GVTabBar = styled.div`
  display: flex;
  gap: 10px;
`;

const GVTab = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 22px;
  border: 1px solid ${({ $active }) => ($active ? '#2b3d54' : 'var(--line)')};
  border-radius: 6px;
  background: ${({ $active }) => ($active ? '#2b3d54' : '#ffffff')};
  color: ${({ $active }) => ($active ? 'var(--white)' : '#a3a6ab')};
  font-family: var(--font-sans, 'DM Sans', sans-serif);
  font-size: 12.5px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink)')};
  }
`;

const QualityContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const QualityAward = styled.span`
  color: var(--ink-muted);
  font-size: 11px;
`;

const QualityIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const QualityIcon = styled.span<{ $green: boolean }>`
  display: inline-flex;
  color: ${({ $green }) => ($green ? '#649f42' : '#d3072b')};

  svg {
    display: block;
    width: 15px;
    height: 15px;
  }
`;

/**
 * V2 Global View page.
 *
 * Uses GET /global_data_view/{wines,pos}/{count,filters} with the shared
 * V2 context filters (channel, brand, market) applied. Mirrors the PO's
 * proto (`data-page="global-view"`): Key Indicators / Points of Sale /
 * Price / Vintages tabs, competitor comparison, saved views and
 * per-table column display.
 */
export const V2GlobalView: React.FC = () => {
  const { user } = useUserMe(true);
  const {
    filters,
    isLoading: isLoadingContext,
    setMarket: setContextMarket,
    setProductType
  } = useV2Filters();
  const { isLoading: isLoadingSurveys, lastCampaigns } = useDashboardData();

  const [activeTab, setActiveTab] = useGvTab();
  const groupBy: GroupBy = user.isBranded ? 'brand' : 'wine';
  const [posStatusChip, setPosStatusChip] = useState<PosStatusChip>('all');
  const comparisonPeriod: V2DistributionPosEvolutionPeriod = 'latest';
  const [search, setSearch] = useState('');
  // Real time POS filter (new — proto's `#gvRealtimeChip`): wired to the
  // real `is_realtime` field. `V2DistributionPosRow` doesn't expose it yet
  // (no backend job populates it), so this honestly filters to 0 rows
  // until that lands — not a bug.
  const [posRealtimeOnly, setPosRealtimeOnly] = useState(false);
  // Rows shown at once, independent of pagination (proto-2's floating
  // "Show 100/500/1000" control) — POS and Raw Data tabs each keep their own.
  const [posRowLimit, setPosRowLimit] =
    useState<(typeof GV_ROW_LIMIT_OPTIONS)[number]>(100);
  const [rawRowLimit, setRawRowLimit] =
    useState<(typeof GV_ROW_LIMIT_OPTIONS)[number]>(100);
  // Expandable brand-level Yes/No sub-columns for "My Brands"/
  // "Competitors" (new — proto's `gvPosExpand`).
  const [posExpand, setPosExpand] = useState({
    myBrands: false,
    competitors: false
  });
  const [page, setPage] = useState(1);

  // ── Sales Tracking mode (mirrors proto's gvPosTrackingToggle): adds a
  // checkbox column to the (still visible) POS table + opens a half-page
  // drawer with the same Action Lists / Activity data as the CRM page,
  // instead of replacing the table with a static summary. ──
  const [trackingOn, setTrackingOnState] = useState(false);
  const [trackingSelectedIds, setTrackingSelectedIds] = useState<
    Set<number | string>
  >(new Set());
  const [trackingDrawerOpen, setTrackingDrawerOpen] = useState(false);
  const [trackingDrawerView, setTrackingDrawerView] = useState<
    'lists' | 'activity'
  >('lists');

  const setTrackingOn = useCallback((on: boolean) => {
    setTrackingOnState(on);
    if (!on) {
      setTrackingSelectedIds(new Set());
      setTrackingDrawerOpen(false);
    }
  }, []);

  const openTrackingDrawer = useCallback((view: 'lists' | 'activity') => {
    setTrackingDrawerView(view);
    setTrackingDrawerOpen(true);
  }, []);

  // ── POS Detail Drawer (mirrors proto's canonical #crm-drawer, opened via
  // the body-level `tr[data-id]` click handler on any page's POS table). ──
  const [selectedPos, setSelectedPos] = useState<PosEntry | null>(null);
  const [posDrawerOpen, setPosDrawerOpen] = useState(false);

  const handlePosRowClick = useCallback((row: any) => {
    setSelectedPos(rowToPosEntry(row));
    setPosDrawerOpen(true);
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && trackingDrawerOpen)
        setTrackingDrawerOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [trackingDrawerOpen]);

  // Shared filter state (reuses the POS filter panel — same field set as
  // proto's "Filter Global View" modal).
  const [storedFilters, setGvFilters] =
    useGvFilters<PosFilterState>(DEFAULT_FILTERS);
  const marketSelectionContext = getMarketSelectionContext(
    filters.channel,
    filters.contentMode
  );
  const { continents, countries, surveyYears, isLoadingGeoFilters } =
    useV2DistributionGeoFilters({
      network: filters.channel === 'on_trade' ? 'on' : 'off',
      premium_store:
        filters.channel === 'on_trade' && filters.contentMode === 'premium'
          ? true
          : undefined
    });
  const isPremiumContext = marketSelectionContext === 'on_premium';
  const nonPremiumYear = getSurveyYearForContext(
    storedFilters,
    marketSelectionContext,
    surveyYears
  );
  const isDateReady =
    !isLoadingGeoFilters &&
    (marketSelectionContext !== 'off' || nonPremiumYear !== '');
  // Premium markets span every survey date ('any'); the date follows them.
  const { isLoadingFilters: isLoadingFilterOptions, filters: apiFilters } =
    useDistributionGlobalDataFilters(
      buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        isPremiumContext ? 'any' : nonPremiumYear
      ),
      isDateReady
    );
  usePruneRefs(
    setGvFilters,
    isDateReady && !isLoadingFilterOptions,
    apiFilters
  );
  const selectedPremiumMarketIds = useMemo(
    () =>
      getMarketsForContext(storedFilters, marketSelectionContext).map(
        (market) => market.value
      ),
    [storedFilters, marketSelectionContext]
  );
  const premiumSurveyYears = useMemo(
    () =>
      getPremiumSurveyYears(apiFilters.markets || [], selectedPremiumMarketIds),
    [apiFilters.markets, selectedPremiumMarketIds]
  );
  const dateOptions = isPremiumContext ? premiumSurveyYears : surveyYears;
  const surveyYear = isPremiumContext
    ? getSurveyYearForContext(
        storedFilters,
        marketSelectionContext,
        premiumSurveyYears
      )
    : nonPremiumYear;
  const headerReferences = (
    user.isBranded ? apiFilters.client_custom_refs : apiFilters.client_refs
  ) as { id: number; name: string }[];
  const gvFilters = useMemo(
    () => ({
      ...filtersForChannel(
        storedFilters,
        filters.channel,
        filters.productType === '2'
      ),
      surveyYear,
      markets: getMarketsForContext(storedFilters, marketSelectionContext)
    }),
    [
      storedFilters,
      filters.channel,
      filters.productType,
      marketSelectionContext,
      surveyYear
    ]
  );
  // Wine/Spirits category toggle (proto's #gvCategoryToggle) — reuses the
  // V2FiltersContext productType state that already feeds every query below
  // (previously a "hidden toggle", see V2FiltersContext.tsx). Switching
  // category resets whichever Product-segment fields don't apply to the
  // new category so a hidden selection never silently keeps filtering.
  const isSpirits = filters.productType === '2';
  const handleProductTypeChange = useCallback(
    (nextIsSpirits: boolean) => {
      setProductType(nextIsSpirits ? '2' : '1');
      setGvFilters((prev) => resetProductTypeFields(prev, nextIsSpirits));
    },
    [setProductType]
  );
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const handleFilterChange = useCallback(
    (updater: (prev: PosFilterState) => PosFilterState) =>
      setGvFilters(updater),
    []
  );
  const handleFilterReset = useCallback(
    () =>
      setGvFilters((previous) => ({
        ...DEFAULT_FILTERS,
        surveyYear: previous.surveyYear,
        surveyYearSelections: previous.surveyYearSelections
      })),
    []
  );

  const singleMarketName = useMemo(
    () => (gvFilters.markets.length === 1 ? gvFilters.markets[0].label : null),
    [gvFilters.markets]
  );

  useEffect(() => {
    if (singleMarketName && singleMarketName !== filters.market) {
      setContextMarket(singleMarketName);
    }
  }, [singleMarketName, filters.market, setContextMarket]);

  const headerMarketOptions = useMemo(
    () =>
      (apiFilters.markets || []).map((market: any) => ({
        value: String(market.id),
        label: market.name
      })),
    [apiFilters.markets]
  );

  const jumpToMarket = useCallback(
    (marketName: string) => {
      const market = (apiFilters.markets || []).find(
        (option: any) => option.name === marketName
      );
      if (!market) return;
      setGvFilters((previous) =>
        setMarketsForContext(previous, marketSelectionContext, [
          { value: String(market.id), label: market.name }
        ])
      );
      setContextMarket(market.name);
    },
    [apiFilters.markets, marketSelectionContext, setContextMarket]
  );

  const activeFilterCount = useMemo(
    () =>
      countActiveFilters(gvFilters, filters.channel === 'on_trade', isSpirits),
    [gvFilters, filters.channel, isSpirits]
  );

  const appliedChips = useMemo<AppliedChip[]>(() => {
    const chips: AppliedChip[] = [];
    const labelFor = (
      options: Array<{
        id?: string | number;
        value?: string | number;
        name?: string;
        label?: string;
      }>,
      value: string | number
    ) =>
      options.find(
        (option) => String(option.id ?? option.value) === String(value)
      )?.name ??
      options.find(
        (option) => String(option.id ?? option.value) === String(value)
      )?.label ??
      String(value);
    const appendSelections = (
      idPrefix: string,
      category: string,
      selections: Array<{ value: string | number; label: string }>
    ) => {
      selections.forEach((selection) =>
        chips.push({
          id: `${idPrefix}-${selection.value}`,
          category,
          label: selection.label || String(selection.value)
        })
      );
    };
    const missingSurvey = isPremiumContext
      ? getMarketsMissingSurvey(
          apiFilters.markets || [],
          gvFilters.markets.map((m) => m.value),
          surveyYear
        )
      : [];
    gvFilters.markets.forEach((m) =>
      chips.push({
        id: `market-${m.value}`,
        category: 'Market',
        label: String(m.label),
        warning: missingSurvey.includes(String(m.label))
          ? I18n.t('v2GlobalView.contextBar.marketUnavailable', {
              year: surveyYear
            })
          : undefined
      })
    );
    if (gvFilters.continentId !== 'all')
      chips.push({
        id: 'continentId',
        category: 'Geographical area',
        label: labelFor(continents, gvFilters.continentId)
      });
    if (gvFilters.countryId !== 'all')
      chips.push({
        id: 'countryId',
        category: 'Country',
        label: labelFor(countries, gvFilters.countryId)
      });
    if (gvFilters.wineColor !== 'all')
      chips.push({
        id: 'wineColor',
        category: 'Color',
        label:
          (
            {
              '1': 'Red',
              '2': 'Rosé',
              '3': 'Sweet',
              '4': 'White',
              '5': 'Sparkling',
              '6': 'Fortified'
            } as Record<string, string>
          )[gvFilters.wineColor] ?? gvFilters.wineColor
      });
    appendSelections('grape', 'Grape', gvFilters.wineGrapeVariety);
    if (isSpirits && gvFilters.spiritCategory !== 'all')
      chips.push({
        id: 'spiritCategory',
        category: I18n.t('v2PosFilters.spiritCategory'),
        label: labelFor(
          apiFilters.wine_sub_types || [],
          gvFilters.spiritCategory
        )
      });
    if (isSpirits && gvFilters.spiritCountry !== 'all')
      chips.push({
        id: 'spiritCountry',
        category: I18n.t('v2PosFilters.spiritCountry'),
        label: labelFor(
          apiFilters.spirit_countries || [],
          gvFilters.spiritCountry
        )
      });
    if (gvFilters.wineCountry !== 'all')
      chips.push({
        id: 'wineCountry',
        category: 'Country',
        label: labelFor(apiFilters.wine_countries || [], gvFilters.wineCountry)
      });
    if (gvFilters.wineRegion !== 'all')
      chips.push({
        id: 'wineRegion',
        category: 'Wine region',
        label: labelFor(apiFilters.wine_regions || [], gvFilters.wineRegion)
      });
    if (gvFilters.wineFormatGroup !== 'all')
      chips.push({
        id: 'wineFormatGroup',
        category: 'Format',
        label: labelFor(
          (isSpirits
            ? apiFilters.spirit_format_groups
            : apiFilters.wine_format_groups) || [],
          gvFilters.wineFormatGroup
        )
      });
    appendSelections('spiritFormatSize', 'Format', gvFilters.spiritFormatSize);
    if (gvFilters.wineAppelation !== 'all')
      chips.push({
        id: 'wineAppelation',
        category: 'Appellation',
        label: labelFor(
          apiFilters.wine_appelations || [],
          gvFilters.wineAppelation
        )
      });
    appendSelections('vintage', 'Vintage', gvFilters.vintage);
    appendSelections('clientRef', 'Client ref', gvFilters.clientRefs);
    appendSelections(
      'clientCustomRef',
      'Client ref',
      gvFilters.clientCustomRefs
    );
    appendSelections(
      'storePriceBand',
      'Menu price level',
      gvFilters.storePriceBand
    );
    appendSelections(
      'storePrimaryType',
      'POS type / cuisine',
      gvFilters.storePrimaryType
    );
    appendSelections(
      'storeWsAward',
      'Wine Spectator award',
      gvFilters.storeWsAward
    );
    appendSelections(
      'storeMichelinStar',
      'Michelin stars',
      gvFilters.storeMichelinStar
    );
    appendSelections(
      'storeWorldsBest',
      "World's 50 Best",
      gvFilters.storeWorldsBest
    );
    appendSelections('storeTopBar', 'Top bars', gvFilters.storeTopBar);
    appendSelections(
      'storeSpiritedAward',
      'Spirited Awards',
      gvFilters.storeSpiritedAward
    );
    if (gvFilters.posStatus !== 'all')
      chips.push({
        id: 'posStatus',
        category: 'Status',
        label:
          (
            { pos: 'Active', conquer: 'To Win Over', lost: 'Lost' } as Record<
              string,
              string
            >
          )[gvFilters.posStatus] ?? gvFilters.posStatus
      });
    if (gvFilters.rangePrices[0] > 0 || gvFilters.rangePrices[1] < 10000)
      chips.push({
        id: 'rangePrices',
        category: 'Medium price per POS',
        label: `${gvFilters.rangePrices[0].toLocaleString()}–${gvFilters.rangePrices[1].toLocaleString()}`
      });
    return chips;
  }, [
    apiFilters,
    continents,
    countries,
    gvFilters,
    isPremiumContext,
    isSpirits,
    surveyYear
  ]);

  const handleRemoveChip = useCallback(
    (chipId: string) => {
      setGvFilters((prev) => {
        const next = { ...prev };
        if (chipId === 'continentId') {
          next.continentId = 'all';
          next.countryId = 'all';
        } else if (chipId === 'countryId') next.countryId = 'all';
        else if (chipId === 'wineColor') next.wineColor = 'all';
        else if (chipId === 'wineCountry') next.wineCountry = 'all';
        else if (chipId === 'wineRegion') next.wineRegion = 'all';
        else if (chipId === 'spiritCategory') next.spiritCategory = 'all';
        else if (chipId === 'spiritCountry') next.spiritCountry = 'all';
        else if (chipId === 'wineFormatGroup') next.wineFormatGroup = 'all';
        else if (chipId === 'wineAppelation') next.wineAppelation = 'all';
        else if (chipId === 'posStatus') next.posStatus = 'all';
        else if (chipId === 'rangePrices') next.rangePrices = [0, 10000];
        else if (chipId.startsWith('market-'))
          return setMarketsForContext(
            next,
            marketSelectionContext,
            getMarketsForContext(next, marketSelectionContext).filter(
              (m) => `market-${m.value}` !== chipId
            )
          );
        else if (chipId.startsWith('grape-'))
          next.wineGrapeVariety = next.wineGrapeVariety.filter(
            (option) => `grape-${option.value}` !== chipId
          );
        else if (chipId.startsWith('vintage-'))
          next.vintage = next.vintage.filter(
            (option) => `vintage-${option.value}` !== chipId
          );
        else if (chipId.startsWith('clientRef-'))
          next.clientRefs = next.clientRefs.filter(
            (option) => `clientRef-${option.value}` !== chipId
          );
        else if (chipId.startsWith('clientCustomRef-'))
          next.clientCustomRefs = next.clientCustomRefs.filter(
            (option) => `clientCustomRef-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storePriceBand-'))
          next.storePriceBand = next.storePriceBand.filter(
            (option) => `storePriceBand-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storePrimaryType-'))
          next.storePrimaryType = next.storePrimaryType.filter(
            (option) => `storePrimaryType-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storeWsAward-'))
          next.storeWsAward = next.storeWsAward.filter(
            (option) => `storeWsAward-${option.value}` !== chipId
          );
        else if (chipId.startsWith('spiritFormatSize-'))
          next.spiritFormatSize = next.spiritFormatSize.filter(
            (option) => `spiritFormatSize-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storeWorldsBest-'))
          next.storeWorldsBest = next.storeWorldsBest.filter(
            (option) => `storeWorldsBest-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storeTopBar-'))
          next.storeTopBar = next.storeTopBar.filter(
            (option) => `storeTopBar-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storeSpiritedAward-'))
          next.storeSpiritedAward = next.storeSpiritedAward.filter(
            (option) => `storeSpiritedAward-${option.value}` !== chipId
          );
        else if (chipId.startsWith('storeMichelinStar-'))
          next.storeMichelinStar = next.storeMichelinStar.filter(
            (option) => `storeMichelinStar-${option.value}` !== chipId
          );
        return next;
      });
    },
    [marketSelectionContext]
  );

  // ── Competitor picker (new — from proto's #gvCompetitorSection) ──
  const {
    competitors,
    setCompetitors,
    competitorOptions,
    customRivalOptions,
    selectedRivalFilter
  } = useGvCompetitorScope({
    isBranded: user.isBranded,
    apiFilters,
    clientRefs: gvFilters.clientRefs,
    clientCustomRefs: gvFilters.clientCustomRefs,
    ready: isDateReady && !isLoadingFilterOptions
  });

  // ── Saved views (new — localStorage-backed, from proto's #gvSavedViews*) ──
  const [savedViews, setSavedViews] = useState<SavedView[]>(loadSavedViews);
  const [savedViewsOpen, setSavedViewsOpen] = useState(false);
  const [savedViewsSearch, setSavedViewsSearch] = useState('');
  const [savedViewsPos, setSavedViewsPos] = useState({ top: 0, left: 0 });
  const savedViewsBtnRef = useRef<HTMLButtonElement>(null);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');

  // Anchor the Saved Views popover just below its trigger button, matching
  // proto's openPopover() (rect.bottom + 10, clamped so it stays on-screen).
  const toggleSavedViews = useCallback(() => {
    setSavedViewsOpen((value) => {
      const next = !value;
      if (next && savedViewsBtnRef.current) {
        const rect = savedViewsBtnRef.current.getBoundingClientRect();
        const width = 360;
        setSavedViewsPos({
          top: rect.bottom + 10,
          left: Math.max(
            12,
            Math.min(rect.left, window.innerWidth - width - 20)
          )
        });
      }
      if (!next) setSavedViewsSearch('');
      return next;
    });
  }, []);

  useEffect(() => {
    if (!savedViewsOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSavedViewsOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [savedViewsOpen]);

  const filteredSavedViews = useMemo(() => {
    const q = savedViewsSearch.trim().toLowerCase();
    if (!q) return savedViews;
    return savedViews.filter((v) => v.name.toLowerCase().includes(q));
  }, [savedViews, savedViewsSearch]);

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    const view: SavedView = {
      id: `view-${Date.now()}`,
      name: newViewName.trim(),
      createdAt: new Date().toISOString(),
      filters: gvFilters,
      productType: filters.productType,
      comparisonPeriod
    };
    const next = [...savedViews, view];
    setSavedViews(next);
    persistSavedViews(next);
    setNewViewName('');
    setSaveViewModalOpen(false);
  };

  const applySavedView = (view: SavedView) => {
    setProductType(view.productType);
    setGvFilters(view.filters);
    setSavedViewsOpen(false);
  };

  const deleteSavedView = (id: string) => {
    const next = savedViews.filter((v) => v.id !== id);
    setSavedViews(next);
    persistSavedViews(next);
  };

  // ── Key Indicators data (GET /v2/global-view/wines) ──
  const keyQueryFilter = useMemo<DistributionGlobalDataFilter>(
    () => ({
      client_and_rivals_only: true,
      use_custom_brands: user.isBranded,
      ...buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        gvFilters.surveyYear
      ),
      aggregation_type: groupBy === 'wine' ? 'wine' : 'brand',
      market_id:
        gvFilters.markets.length > 0
          ? gvFilters.markets.map((m) => Number(m.value))
          : null,
      continent_id:
        gvFilters.continentId !== 'all' ? Number(gvFilters.continentId) : null,
      country_id:
        gvFilters.countryId !== 'all' ? Number(gvFilters.countryId) : null,
      ...buildProductTypeQueryFields(gvFilters, isSpirits),
      wine_type_id: Number(filters.productType),
      vintage:
        gvFilters.vintage.length > 0
          ? gvFilters.vintage.map((v) => String(v.value))
          : null,
      store_menu_price_band_id:
        gvFilters.storePriceBand.length > 0
          ? gvFilters.storePriceBand.map((p) => Number(p.value))
          : null,
      store_primary_sub_type_id: toNumericIds(gvFilters.storePrimaryType),
      store_ws_award:
        gvFilters.storeWsAward.length > 0
          ? gvFilters.storeWsAward.map((a) => String(a.value))
          : null,
      store_star:
        gvFilters.storeMichelinStar.length > 0
          ? gvFilters.storeMichelinStar.map((s) => String(s.value))
          : null,
      ...buildSpiritAwardQueryFields(gvFilters),
      ...selectedRivalFilter,
      store_review_rating_id: null,
      ...buildSelectedRefQuery(gvFilters),
      rangePrices:
        gvFilters.rangePrices[0] > 0 || gvFilters.rangePrices[1] < 10000
          ? gvFilters.rangePrices
          : null,
      period: comparisonPeriod
    }),
    [
      filters.channel,
      filters.contentMode,
      filters.productType,
      isSpirits,
      groupBy,
      user.isBranded,
      comparisonPeriod,
      gvFilters,
      selectedRivalFilter
    ]
  );
  const { data: keyData = [], isLoadingFilters: isLoadingKey } =
    useV2GlobalViewWines(keyQueryFilter, isDateReady && activeTab === 'key');

  const marketPositioningFilter = useMemo(
    () => ({
      ...keyQueryFilter,
      client_and_rivals_only: false,
      use_custom_brands: false,
      selected_client_ref_ids: null,
      selected_client_custom_ids: null
    }),
    [keyQueryFilter]
  );

  const [expandedBrands, setExpandedBrands] = useState<Set<string | number>>(
    new Set()
  );
  const toggleExpandedBrand = useCallback((key: string | number) => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    setExpandedBrands(new Set());
  }, [groupBy, keyQueryFilter]);

  const filteredKeyData = useMemo(() => {
    if (!search) return keyData as any[];
    const term = search.toLowerCase();
    return (keyData as any[]).filter((r: any) =>
      (r.entity_name || '').toLowerCase().includes(term)
    );
  }, [keyData, search]);

  // ── Points of Sale data (GET /v2/distribution/pos) ──
  const posQueryFilter = useMemo<V2DistributionPosFilter>(
    () => ({
      ...buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        gvFilters.surveyYear
      ),
      market_id:
        gvFilters.markets.length > 0
          ? gvFilters.markets.map((m) => Number(m.value))
          : null,
      continent_id:
        gvFilters.continentId !== 'all' ? Number(gvFilters.continentId) : null,
      country_id:
        gvFilters.countryId !== 'all' ? Number(gvFilters.countryId) : null,
      ...buildProductTypeQueryFields(gvFilters, isSpirits),
      wine_type_id: Number(filters.productType),
      vintage:
        gvFilters.vintage.length > 0
          ? gvFilters.vintage.map((v) => String(v.value))
          : null,
      store_menu_price_band_id:
        gvFilters.storePriceBand.length > 0
          ? gvFilters.storePriceBand.map((p) => Number(p.value))
          : null,
      store_primary_sub_type_id: toNumericIds(gvFilters.storePrimaryType),
      store_ws_award:
        gvFilters.storeWsAward.length > 0
          ? gvFilters.storeWsAward.map((a) => String(a.value))
          : null,
      store_star:
        gvFilters.storeMichelinStar.length > 0
          ? gvFilters.storeMichelinStar.map((s) => String(s.value))
          : null,
      ...buildSpiritAwardQueryFields(gvFilters),
      ...selectedRivalFilter,
      ...buildSelectedRefQuery(gvFilters),
      rangePrices:
        gvFilters.rangePrices[0] > 0 || gvFilters.rangePrices[1] < 10000
          ? gvFilters.rangePrices
          : null,
      pos_status:
        activeTab === 'pos' ? statusFilterFromChip(posStatusChip) : null,
      period: comparisonPeriod,
      search: activeTab === 'pos' ? search || null : null
    }),
    [
      activeTab,
      filters.channel,
      filters.contentMode,
      filters.productType,
      isSpirits,
      gvFilters,
      selectedRivalFilter,
      comparisonPeriod,
      posStatusChip,
      search
    ]
  );
  const posScopeFilter = useMemo<V2DistributionPosFilter>(
    () => ({
      ...posQueryFilter,
      pos_status: null,
      search: null
    }),
    [posQueryFilter]
  );
  const {
    data: posData = [],
    isLoading: isInitialLoadingPos,
    isFetching: isFetchingPos,
    isPlaceholderData: isPosPlaceholder
  } = useV2DistributionPos(
    posQueryFilter,
    isDateReady && (activeTab === 'pos' || activeTab === 'marketpos')
  );
  const isPosScopeRefreshing = useScopeRefreshing(
    isFetchingPos && isPosPlaceholder,
    search
  );
  const isLoadingPos = isInitialLoadingPos || isPosScopeRefreshing;
  const gvCurrency: string =
    (posData as any[])?.[0]?.displayed_currency ||
    (keyData as any[])?.[0]?.displayed_currency ||
    'USD';
  const { posCount } = useV2DistributionPosCount(
    {
      ...posScopeFilter,
      selected_client_ref_ids: null,
      selected_client_custom_ids: null,
      selected_rival_ref_ids: null,
      selected_rival_custom_ids: null
    },
    isDateReady
  );
  const { summary: posStatusSummary } = useV2DistributionPosStatusSummary(
    posScopeFilter,
    isDateReady && activeTab === 'pos'
  );

  // CRM tracking data for action lists and activity.
  const {
    actionLists,
    activity,
    refetch: refetchCrmPipeline
  } = useCrmPipeline(
    {
      market: filters.market,
      channel: filters.channel,
      brand: filters.brand
    },
    activeTab === 'pos' && trackingOn
  );
  const createActionList = useCreateActionList();
  const [trackingEmailModalOpen, setTrackingEmailModalOpen] = useState(false);
  const [trackingActionListModalOpen, setTrackingActionListModalOpen] =
    useState(false);
  const statusCounts = {
    all: posCount,
    active: posStatusSummary.active,
    lost: posStatusSummary.lost,
    towin: posStatusSummary.prospect,
    other: posStatusSummary.unknown
  };

  const filteredPosData = useMemo(() => {
    let rows = posData as any[];
    if (posRealtimeOnly) {
      rows = rows.filter((r) => r.is_realtime === true);
    }
    return rows;
  }, [posData, posRealtimeOnly]);

  // POS rows currently selected in Sales Tracking mode, in the minimal shape
  // needed by the "Write an email"/"Save as Action List" modals (the V1
  // distribution rows backing this table don't carry the full V2 `PosEntry`
  // shape used elsewhere in CRM Sales Tracking).
  const trackingSelectedPos = useMemo(
    () =>
      (posData as any[])
        .filter((row) => trackingSelectedIds.has(row.entity_id))
        .map((row) => ({
          id: Number(row.entity_id),
          name: row.entity_name ?? '',
          city: row.store_city_name ?? ''
        })),
    [posData, trackingSelectedIds]
  );

  // ── Expandable brand-level Yes/No sub-columns for "My Brands"/
  // "Competitors" (new — proto's `gvPosExpandBrands`): the top N most
  // frequent real brand/wine names, parsed from the real comma-separated
  // `client_refs`/`rival_refs` strings across the currently filtered POS
  // rows. ──
  const posBrandNames = useCallback((value?: string | null): string[] => {
    if (!value) return [];
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }, []);
  const posExpandBrands = useCallback(
    (rows: any[], mine: boolean, limit: number): string[] => {
      const field = mine ? 'client_refs' : 'rival_refs';
      const freq = new Map<string, number>();
      rows.forEach((r) => {
        posBrandNames(r[field]).forEach((name) => {
          freq.set(name, (freq.get(name) || 0) + 1);
        });
      });
      return Array.from(freq.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, limit)
        .map(([name]) => name);
    },
    [posBrandNames]
  );
  const posMyBrandsExpanded = useMemo(
    () => (posExpand.myBrands ? posExpandBrands(filteredPosData, true, 8) : []),
    [posExpand.myBrands, filteredPosData, posExpandBrands]
  );
  const posCompetitorsExpanded = useMemo(
    () =>
      posExpand.competitors ? posExpandBrands(filteredPosData, false, 10) : [],
    [posExpand.competitors, filteredPosData, posExpandBrands]
  );

  /** Quality Criteria cell (new — proto's `gvPosQuality`): folds the real
   * Michelin (`store_star`) and Wine Spectator (`ws_award`) fields that
   * used to be standalone columns into one column. */
  const renderPosQuality = (row: any) => {
    const starValue = String(row.store_star ?? '').toLowerCase();
    const michelinStars = Number(starValue.match(/^[1-3]/)?.[0] ?? 0);
    const hasGreenStar = starValue.includes('green star');
    const hasQuality = michelinStars > 0 || hasGreenStar || row.ws_award;

    if (!hasQuality) return <NoData>—</NoData>;

    return (
      <QualityContent>
        {row.ws_award && <QualityAward>{row.ws_award}</QualityAward>}
        {(michelinStars > 0 || hasGreenStar) && (
          <QualityIcons>
            {Array.from({ length: michelinStars }, (_, index) => (
              <QualityIcon
                key={`michelin-${index}`}
                role="img"
                aria-label={`Michelin star ${index + 1}`}
                title={`Michelin star ${index + 1}`}
                $green={false}
              >
                <Icon icon="michelinStarIcon" alt="Michelin star" />
              </QualityIcon>
            ))}
            {hasGreenStar && (
              <QualityIcon
                role="img"
                aria-label="Michelin Green Star"
                title="Michelin Green Star"
                $green
              >
                <Icon icon="michelinGreenStarIcon" alt="Michelin Green Star" />
              </QualityIcon>
            )}
          </QualityIcons>
        )}
      </QualityContent>
    );
  };

  const rawQueryFilter = useMemo<V2DistributionRawFilter>(() => {
    // Raw Data's endpoint has no spirit_format_group_id column (see
    // V2DistributionRawFilter's Omit) — the Format field still switches to
    // spirit format options in the modal, but that value only applies to
    // the Key Indicators / Market Overview / Points of Sale views.
    const {
      spirit_format_group_id: _unusedSpiritFormatGroupId,
      ...productTypeFields
    } = buildProductTypeQueryFields(gvFilters, isSpirits);
    return {
      ...buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        gvFilters.surveyYear
      ),
      market_id:
        gvFilters.markets.length > 0
          ? gvFilters.markets.map((m) => Number(m.value))
          : null,
      continent_id:
        gvFilters.continentId !== 'all' ? Number(gvFilters.continentId) : null,
      country_id:
        gvFilters.countryId !== 'all' ? Number(gvFilters.countryId) : null,
      ...productTypeFields,
      wine_type_id: Number(filters.productType),
      vintage:
        gvFilters.vintage.length > 0
          ? gvFilters.vintage.map((v) => String(v.value))
          : null,
      store_menu_price_band_id:
        gvFilters.storePriceBand.length > 0
          ? gvFilters.storePriceBand.map((p) => Number(p.value))
          : null,
      store_primary_sub_type_id: toNumericIds(gvFilters.storePrimaryType),
      store_ws_award:
        gvFilters.storeWsAward.length > 0
          ? gvFilters.storeWsAward.map((a) => String(a.value))
          : null,
      store_star:
        gvFilters.storeMichelinStar.length > 0
          ? gvFilters.storeMichelinStar.map((s) => String(s.value))
          : null,
      ...buildSpiritAwardQueryFields(gvFilters),
      ...selectedRivalFilter,
      ...buildSelectedRefQuery(gvFilters),
      rangePrices:
        gvFilters.rangePrices[0] > 0 || gvFilters.rangePrices[1] < 10000
          ? gvFilters.rangePrices
          : null,
      period: comparisonPeriod,
      search: search || null
    };
  }, [
    filters.channel,
    filters.contentMode,
    filters.productType,
    gvFilters,
    selectedRivalFilter,
    comparisonPeriod,
    search
  ]);
  const {
    data: rawRows = [],
    isLoading: isInitialLoadingRaw,
    isFetching: isFetchingRaw,
    isPlaceholderData: isRawPlaceholder
  } = useV2DistributionRaw(rawQueryFilter, isDateReady && activeTab === 'raw');
  const isRawScopeRefreshing = useScopeRefreshing(
    isFetchingRaw && isRawPlaceholder,
    search
  );
  const isLoadingRaw = isInitialLoadingRaw || isRawScopeRefreshing;

  const isLoading = isLoadingContext || isLoadingFilterOptions;
  // Prerequisite queries (context, survey dates, filter options): until they
  // resolve nothing below the header can render, so the whole view shows a
  // page-level loading state instead of per-table loaders.
  const hasBootstrappedRef = useRef(false);
  const isPrerequisiteLoading =
    isLoadingContext || isLoadingGeoFilters || isLoadingFilterOptions;
  if (!isPrerequisiteLoading) hasBootstrappedRef.current = true;
  // After the first load, context changes (channel, survey year...) only
  // refresh the tables, so tab state is preserved.
  const isBootstrapping = isPrerequisiteLoading && !hasBootstrappedRef.current;

  // ── Column display (new — mirrors gvKeyColModal / gvPosColModal) ──
  const [keyColVisibility, setKeyColVisibility] = useState(
    defaultVisibility(KEY_COLUMNS)
  );
  const [keyColModalOpen, setKeyColModalOpen] = useState(false);
  const [posColVisibility, setPosColVisibility] = useState(
    defaultVisibility(GV_POS_COLUMNS)
  );
  const [posColModalOpen, setPosColModalOpen] = useState(false);
  const rawColumns = useMemo(() => getRawColumns(), []);
  const [rawColVisibility, setRawColVisibility] = useState(() =>
    defaultVisibility(rawColumns)
  );
  const [rawColModalOpen, setRawColModalOpen] = useState(false);

  const keySort = useTableSort(filteredKeyData, (row: any, key) => {
    const values: Record<string, unknown> = {
      name: row.entity_name,
      color: row.color,
      nd: row.dn,
      pr: row.penetration_rate,
      abv: row.avg_visibility,
      posLost: row.lost_pos,
      posGained: row.won_pos,
      price: row.median_price ?? row.av_price,
      loyalty: row.loyalty_rate,
      consumption: row.consumption_rate,
      vintage: row.most_present_vintage,
      older: row.dn_older_vintages
    };
    return values[key];
  });
  const posSort = useTableSort(filteredPosData, (row: any, key) => {
    const clientCount = countRefs(row.client_refs);
    const rivalCount = countRefs(row.rival_refs);
    const values: Record<string, unknown> = {
      name: row.entity_name,
      myBrands: clientCount > 0,
      clientRefs: clientCount,
      competitors: rivalCount,
      city: row.store_city_name,
      quality: `${row.ws_award ?? ''} ${row.store_star ?? ''}`,
      contact: row.store_phone ?? row.store_address ?? row.store_url,
      price: row.av_price,
      totalRefs: row.facing ?? clientCount + rivalCount,
      market: row.market_name,
      status: row.pos_status,
      combinedRefs: clientCount + rivalCount,
      clientList: row.client_refs,
      rivalRefs: rivalCount,
      compList: row.rival_refs
    };
    if (key.startsWith('myBrand:')) {
      return posBrandNames(row.client_refs).includes(key.slice(8));
    }
    if (key.startsWith('competitor:')) {
      return posBrandNames(row.rival_refs).includes(key.slice(11));
    }
    return values[key];
  });
  const rawSort = useTableSort(rawRows, (row, key) => {
    const values: Record<string, unknown> = {
      date: row.survey_year,
      market: row.market_name,
      pos: row.store_name,
      wine: row.wine_name ?? row.marque_name,
      vintage: row.vintage,
      format: formatRawWineFormat(row.wine_format_group_id),
      price: row.price,
      evol: row.evolution_last
    };
    return values[key];
  });

  // ── Pagination ──
  const keyTotalPages = Math.max(
    1,
    Math.ceil(filteredKeyData.length / PAGE_SIZE)
  );
  const paginatedKeyData = useMemo(
    () => keySort.sortedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [keySort.sortedRows, page]
  );

  const renderPercent = (value?: number | null) =>
    value == null ? <NoData>—</NoData> : `${value.toFixed(0)}%`;
  const renderDecimal = (value?: number | null) =>
    value == null ? <NoData>—</NoData> : value.toFixed(1);
  const renderPrice = (row: any) => {
    const value = row.median_price ?? row.av_price;
    if (value == null) return <NoData>—</NoData>;
    return `${Number(value).toFixed(0)} ${row.displayed_currency || 'USD'}`;
  };
  const renderKeyMetricCells = (row: any) => (
    <>
      {keyColVisibility.nd && <TdNum>{row.dn != null ? row.dn : '—'}</TdNum>}
      {keyColVisibility.pr && (
        <TdNum>{renderPercent(row.penetration_rate)}</TdNum>
      )}
      {keyColVisibility.abv && (
        <TdNum>{renderDecimal(row.avg_visibility)}</TdNum>
      )}
      {keyColVisibility.posLost && (
        <TdNum $negative={Number(row.lost_pos) > 0}>
          {row.lost_pos == null
            ? '—'
            : row.lost_pos > 0
              ? `-${row.lost_pos}`
              : 0}
        </TdNum>
      )}
      {keyColVisibility.posGained && (
        <TdNum $positive={Number(row.won_pos) > 0}>
          {row.won_pos == null ? '—' : row.won_pos > 0 ? `+${row.won_pos}` : 0}
        </TdNum>
      )}
      {keyColVisibility.price && <TdNum>{renderPrice(row)}</TdNum>}
      {keyColVisibility.loyalty && (
        <TdNum>{renderPercent(row.loyalty_rate)}</TdNum>
      )}
      {keyColVisibility.consumption && (
        <TdNum>{renderPercent(row.consumption_rate)}</TdNum>
      )}
      {keyColVisibility.vintage && (
        <Td>{row.most_present_vintage || <NoData>—</NoData>}</Td>
      )}
      {keyColVisibility.older && <TdNum>{row.dn_older_vintages ?? '—'}</TdNum>}
    </>
  );
  const visiblePosData = useMemo(
    () => posSort.sortedRows.slice(0, posRowLimit),
    [posSort.sortedRows, posRowLimit]
  );
  const limitedRawRows = useMemo(
    () => rawSort.sortedRows.slice(0, rawRowLimit),
    [rawSort.sortedRows, rawRowLimit]
  );
  const rawTotalPages = Math.max(
    1,
    Math.ceil(limitedRawRows.length / PAGE_SIZE)
  );
  const paginatedRawRows = useMemo(
    () => limitedRawRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [limitedRawRows, page]
  );
  const rawMarketLabel = useMemo(() => {
    const markets = Array.from(
      new Set(
        rawRows
          .map((row) => row.market_name)
          .filter((market): market is string => Boolean(market))
      )
    );
    return markets.length === 1 ? markets[0] : null;
  }, [rawRows]);
  const rawFormatOptions = useMemo(
    () => [
      { value: 'all', label: I18n.t('placeholderAll') },
      ...(
        (filters.productType === '2'
          ? apiFilters.spirit_format_groups
          : apiFilters.wine_format_groups) || []
      ).map((format: any) => ({
        value: String(format.id),
        label: format.name
      }))
    ],
    [
      apiFilters.spirit_format_groups,
      apiFilters.wine_format_groups,
      filters.productType
    ]
  );

  const toggleTrackingSelection = (
    id: number | string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setTrackingSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTrackingSelectAll = () => {
    setTrackingSelectedIds((prev) =>
      prev.size === visiblePosData.length
        ? new Set()
        : new Set(visiblePosData.map((r: any) => r.entity_id ?? 0))
    );
  };

  const clearTrackingSelection = () => setTrackingSelectedIds(new Set());

  // ── Vintage and Price: one selection drives both charts and the
  // per-vintage indicators. ──
  const [priceWine, setPriceWine] = useGvPriceWine();
  const [priceCompare, setPriceCompare] = useState<PriceCompareMode>('none');
  const priceCompetitorOptions = useMemo(() => {
    if (user.isBranded) {
      if (priceWine == null) return customRivalOptions;
      const brandRivals = new Set(
        apiFilters.client_custom_rival_refs[String(priceWine)] ?? []
      );
      return customRivalOptions.filter((option) =>
        brandRivals.has(Number(option.value))
      );
    }
    const rivalIds = new Set(
      apiFilters.client_rival_refs[String(priceWine)] ?? []
    );
    return apiFilters.rival_refs
      .filter((reference) => rivalIds.has(reference.id))
      .map((reference) => ({
        value: String(reference.id),
        label: reference.name
      }));
  }, [
    user.isBranded,
    customRivalOptions,
    apiFilters.client_custom_rival_refs,
    apiFilters.client_rival_refs,
    apiFilters.rival_refs,
    priceWine
  ]);
  const selectedPriceCompetitorIds = useMemo(
    () =>
      priceCompetitorOptions
        .filter((option) => competitors.includes(option.value))
        .map((option) => Number(option.value)),
    [priceCompetitorOptions, competitors]
  );
  const priceCompetitorIds = useMemo(
    () => (user.isBranded ? [] : selectedPriceCompetitorIds),
    [user.isBranded, selectedPriceCompetitorIds]
  );
  const priceCompetitorCustomIds = useMemo(
    () => (user.isBranded ? selectedPriceCompetitorIds : []),
    [user.isBranded, selectedPriceCompetitorIds]
  );
  useEffect(() => {
    if (priceCompare !== 'competitor' || selectedPriceCompetitorIds.length > 0)
      return;
    // Default to the wine's first rival, or leave competitor mode if none.
    const first = priceCompetitorOptions[0]?.value;
    if (first) setCompetitors((prev) => [...prev, first]);
    else setPriceCompare('none');
  }, [priceCompare, selectedPriceCompetitorIds, priceCompetitorOptions]);
  const [priceVintageFrom, setPriceVintageFrom] = useState('all');
  const [priceVintageTo, setPriceVintageTo] = useState('all');
  // "By Vintage" (shared selection) vs "By Product / Brand" (all products,
  // independent of the selection above) — proto-2's `gvVintViewSwitch`.
  const [vintPanel, setVintPanel] = useState<'vintage' | 'brand'>('vintage');
  const { data: vintBrandData = [], isLoadingFilters: isLoadingVintBrandData } =
    useV2GlobalViewWines(
      { ...marketPositioningFilter, aggregation_type: 'brand' },
      isDateReady && activeTab === 'price' && vintPanel === 'brand'
    );
  const filteredVintBrandData = useMemo(() => {
    if (!search) return vintBrandData as any[];
    const term = search.toLowerCase();
    return (vintBrandData as any[]).filter((row) =>
      (row.entity_name || '').toLowerCase().includes(term)
    );
  }, [search, vintBrandData]);
  const wineOptions = useMemo(
    () =>
      (headerReferences || []).map((r: any) => ({
        value: r.id,
        label: r.name
      })),
    [headerReferences]
  );
  const hasInitializedChartWines = useRef(false);

  useEffect(() => {
    if (hasInitializedChartWines.current || wineOptions.length === 0) return;
    hasInitializedChartWines.current = true;
    const firstWine = wineOptions[0].value;
    setPriceWine(firstWine);
  }, [wineOptions]);

  // ── Price tab: wire own wine + comparison selections to the real endpoint ──
  const priceByVintageParams = useMemo(
    () => ({
      ...buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        gvFilters.surveyYear
      ),
      ...buildPriceByVintageParams({
        gvFilters,
        productTypeId: filters.productType,
        priceWine,
        priceCompare,
        competitorIds: priceCompetitorIds,
        competitorCustomIds: priceCompetitorCustomIds,
        useCustomBrands: user.isBranded
      }),
      period: comparisonPeriod
    }),
    [
      gvFilters,
      filters.channel,
      filters.contentMode,
      filters.productType,
      priceWine,
      priceCompare,
      priceCompetitorIds,
      priceCompetitorCustomIds,
      user.isBranded,
      comparisonPeriod
    ]
  );
  const hasPriceSelection = priceWine != null;
  const {
    isLoading: isPriceLoading,
    isError: isPriceError,
    error: priceError,
    series: priceSeries,
    displayedCurrency: priceCurrency,
    refetch: refetchPriceByVintage
  } = usePriceByVintage(
    priceByVintageParams,
    isDateReady && activeTab === 'price' && hasPriceSelection
  );
  const pricePrimarySeries =
    priceSeries.find((series) => series.isOwnBrand) ?? priceSeries[0] ?? null;
  const priceCompareSeries =
    priceSeries.find((series) => !series.isOwnBrand) ?? null;
  const priceVintageOptions = useMemo(
    () => [
      { value: 'all', label: I18n.t('placeholderAll') },
      ...sortVintages(
        (pricePrimarySeries?.points ?? [])
          .map((point) => point.vintage)
          .filter(
            (vintage) =>
              vintage.toLowerCase() !== 'all' && vintage.toLowerCase() !== 'nv'
          )
      ).map((vintage) => ({ value: vintage, label: vintage }))
    ],
    [pricePrimarySeries]
  );
  const priceChartData = useMemo(() => {
    if (!pricePrimarySeries) return [];
    const primaryByVintage = new Map(
      pricePrimarySeries.points
        .filter((point) => point.vintage.toLowerCase() !== 'all')
        .map((point) => [point.vintage, point])
    );
    const compareByVintage = new Map(
      (priceCompareSeries?.points ?? []).map((point) => [point.vintage, point])
    );
    const vintages = sortVintages(Array.from(primaryByVintage.keys())).filter(
      (vintage) =>
        isVintageWithinRange(vintage, priceVintageFrom, priceVintageTo)
    );
    const rows = vintages.map((vintage) => {
      const primaryPoint = primaryByVintage.get(vintage);
      const previousPoint = getPreviousVintagePoint(vintage, primaryByVintage);
      const comparisonPoint =
        priceCompare === 'previous'
          ? previousPoint
          : compareByVintage.get(vintage);
      return {
        vintage,
        value: primaryPoint?.medianPrice ?? null,
        compare:
          priceCompare !== 'none' ? comparisonPoint?.medianPrice ?? null : null,
        minPrice: primaryPoint?.minPrice ?? null,
        medianPrice: primaryPoint?.medianPrice ?? null,
        maxPrice: primaryPoint?.maxPrice ?? null,
        facing: primaryPoint?.facing ?? null,
        posCount: primaryPoint?.posCount ?? null
      };
    });
    const average = (values: Array<number | null>) => {
      const pricedValues = values.filter(
        (value): value is number => value != null
      );
      if (pricedValues.length === 0) return null;
      return (
        pricedValues.reduce((sum, value) => sum + value, 0) /
        pricedValues.length
      );
    };
    return [
      {
        vintage: I18n.t('placeholderAll'),
        value: average(rows.map((row) => row.value)),
        compare:
          priceCompare !== 'none'
            ? average(rows.map((row) => row.compare))
            : null,
        minPrice: null,
        medianPrice: null,
        maxPrice: null,
        facing: null,
        posCount: null
      },
      ...rows
    ];
  }, [
    pricePrimarySeries,
    priceCompareSeries,
    priceCompare,
    priceVintageFrom,
    priceVintageTo
  ]);
  const priceChartValueLabel =
    pricePrimarySeries?.entityName ||
    wineOptions.find((option) => option.value === priceWine)?.label ||
    I18n.t('globalView.price.selectWine');
  const priceChartCompareLabel =
    priceCompare === 'previous'
      ? I18n.t('globalView.price.previousYearComparison')
      : priceCompareSeries?.entityName ?? null;
  const vintageCompetitorIds = priceCompetitorIds;
  const vintageDistributionParams = useMemo(
    () => ({
      ...buildGlobalViewScope(
        filters.channel,
        filters.contentMode,
        gvFilters.surveyYear
      ),
      ...buildPriceByVintageParams({
        gvFilters,
        productTypeId: filters.productType,
        priceWine,
        priceCompare,
        competitorIds: vintageCompetitorIds,
        competitorCustomIds: priceCompetitorCustomIds,
        useCustomBrands: user.isBranded
      }),
      period: comparisonPeriod
    }),
    [
      gvFilters,
      filters.channel,
      filters.contentMode,
      filters.productType,
      priceWine,
      priceCompare,
      vintageCompetitorIds,
      priceCompetitorCustomIds,
      user.isBranded,
      comparisonPeriod
    ]
  );
  const hasVintageSelection = hasPriceSelection;
  const {
    isLoading: isVintageDistributionLoading,
    isError: isVintageDistributionError,
    error: vintageDistributionError,
    series: vintageSeries,
    refetch: refetchVintageDistribution
  } = useVintageDistribution(
    vintageDistributionParams,
    isDateReady && activeTab === 'price' && hasVintageSelection
  );
  const vintagePrimarySeries =
    vintageSeries.find((series) => series.isOwnBrand) ??
    vintageSeries[0] ??
    null;
  const vintageCompareSeries =
    vintageSeries.find((series) => !series.isOwnBrand) ?? null;
  const vintageChartData = useMemo(() => {
    if (!vintagePrimarySeries) return [];
    const primaryByVintage = new Map(
      vintagePrimarySeries.points
        .filter((point) => point.vintage.toLowerCase() !== 'all')
        .map((point) => [point.vintage, point])
    );
    const compareByVintage = new Map(
      (vintageCompareSeries?.points ?? [])
        .filter((point) => point.vintage.toLowerCase() !== 'all')
        .map((point) => [point.vintage, point])
    );
    const vintages = sortVintages(Array.from(primaryByVintage.keys())).filter(
      (vintage) =>
        isVintageWithinRange(vintage, priceVintageFrom, priceVintageTo)
    );
    return vintages.map((vintage) => {
      const previousPoint = getPreviousVintagePoint(vintage, primaryByVintage);
      return {
        vintage,
        value: primaryByVintage.get(vintage)?.dn ?? 0,
        compare:
          priceCompare === 'previous'
            ? previousPoint?.dn ?? null
            : vintageCompareSeries
              ? compareByVintage.get(vintage)?.dn ?? 0
              : null
      };
    });
  }, [
    vintagePrimarySeries,
    vintageCompareSeries,
    priceCompare,
    priceVintageFrom,
    priceVintageTo
  ]);
  const vintageRecordsAnalysed = useMemo(
    () =>
      vintageChartData.reduce((total, point) => total + (point.value ?? 0), 0),
    [vintageChartData]
  );
  const vintageChartValueLabel =
    vintagePrimarySeries?.entityName ||
    wineOptions.find((option) => option.value === priceWine)?.label ||
    I18n.t('globalView.price.selectWine');
  const vintageChartCompareLabel =
    priceCompare === 'previous'
      ? I18n.t('globalView.price.previousYearComparison')
      : vintageCompareSeries?.entityName ?? null;

  // ── "Numerical Distribution, all selected vintages" KPI row above the ND
  // chart (proto-2's `#gvVintNdKpi`) — aggregate ND for the selected wine,
  // plus the comparison series' aggregate ND when comparing. ──
  const vintageCompareRecordsAnalysed = useMemo(
    () =>
      vintageChartCompareLabel
        ? vintageChartData.reduce(
            (total, point) => total + (point.compare ?? 0),
            0
          )
        : null,
    [vintageChartData, vintageChartCompareLabel]
  );
  const vintageNdKpis = useMemo(() => {
    if (vintageChartData.length === 0) return [];
    const kpis = [
      { label: vintageChartValueLabel, value: vintageRecordsAnalysed }
    ];
    if (vintageChartCompareLabel && vintageCompareRecordsAnalysed != null) {
      kpis.push({
        label: vintageChartCompareLabel,
        value: vintageCompareRecordsAnalysed
      });
    }
    return kpis;
  }, [
    vintageChartData,
    vintageChartValueLabel,
    vintageChartCompareLabel,
    vintageRecordsAnalysed,
    vintageCompareRecordsAnalysed
  ]);

  // ── "Indicators – by Vintage" table (proto-2's `#gvVintYearTable`) — one
  // row per vintage combining own-series prices, consumption and reference counts.
  const vintageYearRows = useMemo(() => {
    if (!vintagePrimarySeries) return [];
    const priceByVintage = new Map(
      (pricePrimarySeries?.points ?? [])
        .filter((point) => point.vintage.toLowerCase() !== 'all')
        .map((point) => [point.vintage, point.medianPrice])
    );
    const competitorFacingByVintage = new Map<string, number>();
    vintageSeries
      .filter((series) => !series.isOwnBrand)
      .forEach((series) => {
        series.points.forEach((point) => {
          if (point.vintage.toLowerCase() === 'all') return;
          competitorFacingByVintage.set(
            point.vintage,
            (competitorFacingByVintage.get(point.vintage) || 0) +
              (point.facing || 0)
          );
        });
      });
    return sortVintages(
      vintagePrimarySeries.points
        .filter((point) => point.vintage.toLowerCase() !== 'all')
        .map((point) => point.vintage)
        .filter((vintage) =>
          isVintageWithinRange(vintage, priceVintageFrom, priceVintageTo)
        )
    ).map((vintage) => {
      const point = vintagePrimarySeries.points.find(
        (p) => p.vintage === vintage
      );
      const pricePoint = pricePrimarySeries?.points.find(
        (p) => p.vintage === vintage
      );
      return {
        vintage,
        minPrice: pricePoint?.minPrice ?? null,
        medianPrice: priceByVintage.get(vintage) ?? null,
        maxPrice: pricePoint?.maxPrice ?? null,
        nd: point?.dn ?? 0,
        brandRefs: point?.facing ?? 0,
        competitorRefs: competitorFacingByVintage.get(vintage) ?? 0,
        posCount: pricePoint?.posCount ?? 0,
        consumptionRate: pricePoint?.consumptionRate ?? null
      };
    });
  }, [
    vintagePrimarySeries,
    pricePrimarySeries,
    vintageSeries,
    priceVintageFrom,
    priceVintageTo
  ]);
  const vintageSort = useTableSort(
    vintageYearRows,
    (row, key) => row[key as keyof typeof row],
    { key: 'vintage', direction: 'descending' }
  );

  // ── "By Product / Brand" panel of the merged Vintage and Price tab —
  // real per-wine data (same source as My Products & Competition), scoped
  // to all products in the current filters, independent of the Wine/
  // vintage/comparison selection above. Avg. Vintage (previous survey),
  // Novelty Rate, Top New Vintages and Most Consumed Vintage have no
  // backing field yet and are shown honestly as "—". ──
  const vintBrandSort = useTableSort(filteredVintBrandData, (row: any, key) => {
    const values: Record<string, unknown> = {
      name: row.entity_name,
      avgVintageLast: null,
      avgVintagePrevious: null,
      consumption: row.consumption_rate,
      novelty: null,
      topNewVintages: null,
      vintage: row.most_present_vintage,
      older: row.dn_older_vintages,
      mostConsumed: null
    };
    return values[key];
  });
  const vintBrandRows = vintBrandSort.sortedRows;

  const [isExporting, setIsExporting] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    variant: 'info' | 'error';
  } | null>(null);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const runExport = useCallback(
    async (
      tab: GdvExportTab,
      queryFilter: object,
      columns: string[],
      extra: Record<string, boolean> = {}
    ) => {
      setIsExporting(true);
      setToast({
        message: I18n.t('v2GlobalView.export.started'),
        variant: 'info'
      });
      try {
        await downloadGdvExcel(
          tab,
          { ...queryFilter, ...extra } as any,
          columns
        );
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        console.error('GDV export failed', tab, error);
        setToast({
          message: `${I18n.t('v2GlobalView.export.error')}${
            status ? ` (HTTP ${status})` : ''
          }`,
          variant: 'error'
        });
      } finally {
        setIsExporting(false);
      }
    },
    []
  );
  const visibleColumnIds = (
    columns: Array<{ id: string }>,
    visibility: Record<string, boolean>
  ) => columns.filter((c) => visibility[c.id]).map((c) => c.id);
  const handleExportKey = useCallback(
    () =>
      runExport(
        'key-indicators',
        { ...keyQueryFilter, search: search || null },
        visibleColumnIds(KEY_COLUMNS, keyColVisibility)
      ),
    [runExport, keyQueryFilter, search, keyColVisibility]
  );
  const handleExportPos = useCallback(
    () =>
      runExport(
        'pos',
        posQueryFilter,
        visibleColumnIds(GV_POS_COLUMNS, posColVisibility),
        {
          expand_my_brands: posExpand.myBrands,
          expand_competitors: posExpand.competitors
        }
      ),
    [runExport, posQueryFilter, posColVisibility, posExpand]
  );
  const handleExportRaw = useCallback(
    () =>
      runExport(
        'raw',
        rawQueryFilter,
        visibleColumnIds(rawColumns, rawColVisibility)
      ),
    [runExport, rawQueryFilter, rawColumns, rawColVisibility]
  );

  const exportVintageIndicators = () => {
    if (vintPanel === 'vintage') {
      downloadCsv(
        'gdv-vintage-indicators.csv',
        [
          I18n.t('globalView.price.indicators.vintage'),
          I18n.t('globalView.price.indicators.medianPrice'),
          I18n.t('globalView.price.indicators.consumptionRate'),
          I18n.t('globalView.price.indicators.brandRefs'),
          ...(priceCompare === 'competitor' &&
          selectedPriceCompetitorIds.length > 0
            ? [I18n.t('globalView.price.indicators.competitorRefs')]
            : [])
        ],
        vintageYearRows.map((row) => [
          row.vintage,
          row.medianPrice,
          row.consumptionRate,
          row.brandRefs,
          ...(priceCompare === 'competitor' &&
          selectedPriceCompetitorIds.length > 0
            ? [row.competitorRefs]
            : [])
        ])
      );
      return;
    }

    downloadCsv(
      'gdv-product-brand-indicators.csv',
      [
        I18n.t('globalView.price.indicators.productBrand'),
        I18n.t('globalView.price.indicators.avgVintageLastSurvey'),
        I18n.t('globalView.price.indicators.avgVintagePreviousSurvey'),
        I18n.t('globalView.price.indicators.consumptionRate'),
        I18n.t('globalView.price.indicators.noveltyRate'),
        I18n.t('globalView.price.indicators.top3NewVintages'),
        I18n.t('globalView.price.indicators.mostPresentVintage'),
        I18n.t('globalView.price.indicators.ndOlderVintages'),
        I18n.t('globalView.price.indicators.mostConsumedVintage')
      ],
      vintBrandRows.map((row: any) => [
        row.entity_name,
        null,
        null,
        row.consumption_rate,
        null,
        null,
        row.most_present_vintage,
        row.dn_older_vintages,
        null
      ])
    );
  };

  return (
    <Page>
      {toast && (
        <ToastBox $variant={toast.variant} role="status">
          {toast.message}
        </ToastBox>
      )}
      <PageHeader
        title={I18n.t('v2GlobalView.header.title')}
        subtitle={I18n.t(
          isSpirits
            ? 'v2GlobalView.header.subtitleSpirit'
            : 'v2GlobalView.header.subtitle'
        )}
        eyebrow={
          <CategoryToggle role="radiogroup" aria-label="Product category">
            <CategoryToggleBtn
              type="button"
              $active={!isSpirits}
              aria-pressed={!isSpirits}
              aria-label="Wine category"
              onClick={() => handleProductTypeChange(false)}
            >
              Wine
            </CategoryToggleBtn>
            <CategoryToggleBtn
              type="button"
              $active={isSpirits}
              aria-pressed={isSpirits}
              aria-label="Spirits category"
              onClick={() => handleProductTypeChange(true)}
            >
              Spirits
            </CategoryToggleBtn>
          </CategoryToggle>
        }
      >
        {!isLoadingSurveys && lastCampaigns.length > 0 && (
          <SurveyCol>
            <SurveyHeading>
              {I18n.t('v2GlobalView.surveys.heading')}
            </SurveyHeading>
            <SurveyStrip>
              {lastCampaigns.slice(0, 3).map((campaign: any, index: number) => (
                <SurveyCard
                  key={campaign.market_name + campaign.network_code + index}
                  type="button"
                  onClick={() => jumpToMarket(campaign.market_name)}
                >
                  <SurveyThumb $bg={campaign.market_image_path || ''}>
                    <SurveyStamp>{campaign.dn_client ?? '—'}</SurveyStamp>
                  </SurveyThumb>
                  <SurveyMeta>
                    <SurveyRow>
                      <SurveyName>{campaign.market_name}</SurveyName>
                      <SurveyBadge>{campaign.network_code}</SurveyBadge>
                    </SurveyRow>
                    <SurveyDate>
                      {formatSurveyDate(campaign.release_date)}
                    </SurveyDate>
                  </SurveyMeta>
                </SurveyCard>
              ))}
            </SurveyStrip>
          </SurveyCol>
        )}
      </PageHeader>

      {isBootstrapping ? (
        <GlobalLoadingState
          title={I18n.t('v2GlobalView.bootstrap.title')}
          subtitle={I18n.t('v2GlobalView.bootstrap.subtitle')}
        />
      ) : (
        <>
          <ContextBarWrapper>
            <ScopeSummary>
              {posCount.toLocaleString()} points of sale in current scope
            </ScopeSummary>
            <GVBar>
              <GVPrimaryRow>
                <GVControlColumn role="group" aria-label="Context and content">
                  <ContextToggleSection
                    stacked
                    label={I18n.t(
                      'v2GlobalView.contextBar.distributionNetwork'
                    )}
                    onTradeLabel={I18n.t('v2GlobalView.contextBar.onTrade')}
                    offTradeLabel={I18n.t('v2GlobalView.contextBar.offTrade')}
                  />
                  <ContentToggleSection
                    stacked
                    label={I18n.t('v2GlobalView.contextBar.accountCoverage')}
                    fullLabel={I18n.t('v2GlobalView.contextBar.full')}
                  />
                </GVControlColumn>
                <GVControlColumn role="group" aria-label="Market selection">
                  <Section $stacked>
                    <Label>
                      {I18n.t('v2GlobalView.contextBar.marketLabel')}
                    </Label>
                    <MarketControl
                      options={headerMarketOptions}
                      selected={gvFilters.markets.map((market) => ({
                        ...market,
                        value: String(market.value)
                      }))}
                      disabled={isLoadingFilterOptions}
                      onChange={(markets) =>
                        setGvFilters((previous) =>
                          setMarketsForContext(
                            previous,
                            marketSelectionContext,
                            markets
                          )
                        )
                      }
                    />
                  </Section>
                </GVControlColumn>
                <GVControlColumn
                  role="group"
                  aria-label="Survey date and comparison period"
                >
                  <Section $stacked>
                    <Label>
                      {I18n.t('v2GlobalView.contextBar.surveyDate')}
                    </Label>
                    <SurveyDateControl
                      context={marketSelectionContext}
                      years={dateOptions}
                      value={surveyYear}
                      disabled={isLoadingGeoFilters || isLoadingFilterOptions}
                      onChange={(year) =>
                        setGvFilters((previous) =>
                          setSurveyYearForContext(
                            previous,
                            marketSelectionContext,
                            year
                          )
                        )
                      }
                    />
                  </Section>
                  <Section $stacked>
                    <Label>
                      {I18n.t('v2GlobalView.contextBar.comparisonPeriod')}
                    </Label>
                    <PeriodToggle />
                  </Section>
                </GVControlColumn>
                <GVScopeControls
                  role="group"
                  aria-label="Brand and competitors"
                >
                  <Section $stacked>
                    <Label>
                      {I18n.t('v2GlobalView.contextBar.productsLabel')}
                    </Label>
                    <ProductControl
                      options={headerReferences.map((reference) => ({
                        value: String(reference.id),
                        label: reference.name
                      }))}
                      selected={(user.isBranded
                        ? gvFilters.clientCustomRefs
                        : gvFilters.clientRefs
                      ).map((ref) => ({
                        value: String(ref.value),
                        label: ref.label
                      }))}
                      onChange={(selection) =>
                        setGvFilters((previous) => ({
                          ...previous,
                          clientRefs: user.isBranded ? [] : selection,
                          clientCustomRefs: user.isBranded ? selection : []
                        }))
                      }
                      disabled={isLoadingFilterOptions}
                    />
                  </Section>
                  <CompetitorSection $stacked>
                    <Label>
                      {I18n.t('v2GlobalView.contextBar.competitorsLabel')}
                    </Label>
                    <MultiChipDropdown
                      options={competitorOptions}
                      selected={competitorOptions.filter((option) =>
                        competitors.includes(option.value)
                      )}
                      onChange={(selected) => {
                        setCompetitors(selected.map((option) => option.value));
                        setPriceCompare(
                          selected.length > 0 ? 'competitor' : 'none'
                        );
                      }}
                      placeholder={I18n.t(
                        'v2GlobalView.contextBar.noCompetitors'
                      )}
                      allLabel={I18n.t('v2GlobalView.contextBar.noCompetitors')}
                      variant="brand"
                      neutral
                    />
                  </CompetitorSection>
                </GVScopeControls>
              </GVPrimaryRow>
              <GVActionRow>
                <SavedViewsBtn
                  ref={savedViewsBtnRef}
                  type="button"
                  aria-haspopup="dialog"
                  aria-expanded={savedViewsOpen}
                  onClick={toggleSavedViews}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                  </svg>
                  Saved Views
                  <FiltersBadge $zero={savedViews.length === 0}>
                    {savedViews.length}
                  </FiltersBadge>
                </SavedViewsBtn>
                <GVFiltersBtn
                  type="button"
                  onClick={() => setFilterModalOpen(true)}
                >
                  <FilterIcon />
                  Filters
                  <FiltersBadge $zero={activeFilterCount === 0}>
                    {activeFilterCount}
                  </FiltersBadge>
                </GVFiltersBtn>
                {activeFilterCount > 0 && (
                  <ResetBtn type="button" onClick={handleFilterReset}>
                    Reset
                  </ResetBtn>
                )}
              </GVActionRow>
              <AppliedFilters
                chips={appliedChips}
                onRemove={handleRemoveChip}
              />
            </GVBar>
          </ContextBarWrapper>

          {/* Saved Views — floating layover anchored under its trigger button
          (prototype's #gvSavedViewsOverlay / #gvSavedViewsPopover), not a
          centered modal and not an inline section pushed into the page flow. */}
          {savedViewsOpen && (
            <>
              <SavedViewsOverlay onClick={() => setSavedViewsOpen(false)} />
              <SavedViewsPopover
                $top={savedViewsPos.top}
                $left={savedViewsPos.left}
                role="dialog"
                aria-label="Saved Views"
              >
                <SavedViewsPopHead>
                  <SavedViewsPopTitle>Saved Views</SavedViewsPopTitle>
                  <SavedViewsCloseBtn
                    type="button"
                    aria-label="Close"
                    onClick={() => setSavedViewsOpen(false)}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </SavedViewsCloseBtn>
                </SavedViewsPopHead>
                <SaveViewTriggerBtn
                  type="button"
                  onClick={() => {
                    setSavedViewsOpen(false);
                    setSaveViewModalOpen(true);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Save current view
                </SaveViewTriggerBtn>
                <SavedViewsSearchInput
                  type="text"
                  placeholder="Search saved views…"
                  value={savedViewsSearch}
                  onChange={(e) => setSavedViewsSearch(e.target.value)}
                />
                <SavedViewsList>
                  {filteredSavedViews.length === 0 ? (
                    <EmptySavedViews>
                      {savedViews.length
                        ? 'No saved views match your search.'
                        : 'No saved views yet — save your current filters to get started.'}
                    </EmptySavedViews>
                  ) : (
                    filteredSavedViews.map((v) => (
                      <SavedViewRow key={v.id}>
                        <SavedViewInfo onClick={() => applySavedView(v)}>
                          <strong>{v.name}</strong>
                          <span>
                            {new Date(v.createdAt).toLocaleDateString()}
                          </span>
                        </SavedViewInfo>
                        <RemoveViewBtn
                          type="button"
                          aria-label="Delete view"
                          title="Delete"
                          onClick={() => deleteSavedView(v.id)}
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </RemoveViewBtn>
                      </SavedViewRow>
                    ))
                  )}
                </SavedViewsList>
              </SavedViewsPopover>
            </>
          )}

          {/* Tabs */}
          <TabSection>
            <GVTabBar>
              {(
                [
                  {
                    id: 'marketpos',
                    label: I18n.t('v2GlobalView.tabs.marketpos')
                  },
                  { id: 'key', label: I18n.t('v2GlobalView.tabs.key') },
                  { id: 'pos', label: `Points of Sale (${posCount})` },
                  {
                    id: 'price',
                    label: I18n.t('globalView.price.tabLabel')
                  },
                  { id: 'raw', label: I18n.t('v2GlobalView.tabs.raw') }
                ] as { id: GVTabId; label: string }[]
              ).map((tab) => (
                <GVTab
                  key={tab.id}
                  type="button"
                  $active={activeTab === tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setPage(1);
                  }}
                >
                  {tab.label}
                </GVTab>
              ))}
            </GVTabBar>
          </TabSection>

          {activeTab === 'marketpos' && isDateReady && (
            <MarketPositioning
              filter={marketPositioningFilter}
              summaryFilter={posScopeFilter}
              posRows={posData as unknown[]}
              isLoadingPos={isLoadingPos}
            />
          )}

          {/* My Products & Competition tab (proto-2: renamed from "My Performance" /
          "Key Indicators" — Market grouping now lives in Market Overview). */}
          {activeTab === 'key' && (
            <KeyIndicatorsTab
              expandedBrands={expandedBrands}
              groupBy={groupBy}
              isLoading={isLoading}
              isExporting={isExporting}
              isLoadingKey={isLoadingKey}
              onExport={handleExportKey}
              keyColVisibility={keyColVisibility}
              keyQueryFilter={keyQueryFilter}
              keySort={keySort}
              keyTotalPages={keyTotalPages}
              page={page}
              paginatedKeyData={paginatedKeyData}
              renderKeyMetricCells={renderKeyMetricCells}
              search={search}
              setKeyColModalOpen={setKeyColModalOpen}
              setPage={setPage}
              setSearch={setSearch}
              toggleExpandedBrand={toggleExpandedBrand}
              user={user}
            />
          )}

          {/* Points of Sale tab */}
          {activeTab === 'pos' && (
            <PointsOfSaleTab
              actionLists={actionLists}
              activity={activity}
              handlePosRowClick={handlePosRowClick}
              isLoading={isLoading}
              isExporting={isExporting}
              isLoadingPos={isLoadingPos}
              onExport={handleExportPos}
              openTrackingDrawer={openTrackingDrawer}
              visiblePosData={visiblePosData}
              posBrandNames={posBrandNames}
              posColVisibility={posColVisibility}
              posCompetitorsExpanded={posCompetitorsExpanded}
              posDrawerOpen={posDrawerOpen}
              posExpand={posExpand}
              posMyBrandsExpanded={posMyBrandsExpanded}
              posRealtimeOnly={posRealtimeOnly}
              posRowLimit={posRowLimit}
              posSort={posSort}
              posStatusChip={posStatusChip}
              renderPosQuality={renderPosQuality}
              search={search}
              selectedPos={selectedPos}
              setPosColModalOpen={setPosColModalOpen}
              setPosDrawerOpen={setPosDrawerOpen}
              setPosExpand={setPosExpand}
              setPosRealtimeOnly={setPosRealtimeOnly}
              setPosRowLimit={setPosRowLimit}
              setPosStatusChip={setPosStatusChip}
              setSearch={setSearch}
              setTrackingOn={setTrackingOn}
              statusCounts={statusCounts}
              toggleTrackingSelectAll={toggleTrackingSelectAll}
              toggleTrackingSelection={toggleTrackingSelection}
              trackingOn={trackingOn}
              trackingSelectedIds={trackingSelectedIds}
            />
          )}

          {/* Price tab — wired to GET /v2/global-view/price-by-vintage */}
          {/* Vintage and Price tab (proto-2: merged Price + Vintages tabs behind
          a "By Vintage" / "By Product & Brand" switch). */}
          {activeTab === 'price' && (
            <VintagePriceTab
              competitors={competitors}
              competitorOptions={priceCompetitorOptions}
              exportVintageIndicators={exportVintageIndicators}
              hasPriceSelection={hasPriceSelection}
              hasVintageSelection={hasVintageSelection}
              isLoadingVintBrandData={isLoadingVintBrandData}
              isPriceError={isPriceError}
              isPriceLoading={isPriceLoading}
              isVintageDistributionError={isVintageDistributionError}
              isVintageDistributionLoading={isVintageDistributionLoading}
              priceChartCompareLabel={priceChartCompareLabel}
              priceChartData={priceChartData}
              priceChartValueLabel={priceChartValueLabel}
              priceCompare={priceCompare}
              priceCurrency={priceCurrency}
              priceError={priceError}
              priceSeries={priceSeries}
              priceVintageFrom={priceVintageFrom}
              priceVintageOptions={priceVintageOptions}
              priceVintageTo={priceVintageTo}
              priceWine={priceWine}
              refetchPriceByVintage={refetchPriceByVintage}
              refetchVintageDistribution={refetchVintageDistribution}
              renderPercent={renderPercent}
              setCompetitors={setCompetitors}
              setPriceCompare={setPriceCompare}
              setPriceVintageFrom={setPriceVintageFrom}
              setPriceVintageTo={setPriceVintageTo}
              setPriceWine={setPriceWine}
              setVintPanel={setVintPanel}
              vintBrandRows={vintBrandRows}
              vintBrandSort={vintBrandSort}
              vintPanel={vintPanel}
              vintageChartCompareLabel={vintageChartCompareLabel}
              vintageChartData={vintageChartData}
              vintageChartValueLabel={vintageChartValueLabel}
              vintageDistributionError={vintageDistributionError}
              vintageNdKpis={vintageNdKpis}
              vintageSeries={vintageSeries}
              vintageSort={vintageSort}
              vintageYearRows={vintageYearRows}
              wineOptions={wineOptions}
            />
          )}

          {activeTab === 'raw' && (
            <RawDataTab
              gvFilters={gvFilters}
              isExporting={isExporting}
              isFetchingRaw={isFetchingRaw}
              onExport={handleExportRaw}
              isLoadingRaw={isLoadingRaw}
              limitedRawRows={limitedRawRows}
              page={page}
              paginatedRawRows={paginatedRawRows}
              rawColVisibility={rawColVisibility}
              rawFormatOptions={rawFormatOptions}
              rawMarketLabel={rawMarketLabel}
              rawRowLimit={rawRowLimit}
              rawRows={rawRows}
              rawSort={rawSort}
              rawTotalPages={rawTotalPages}
              search={search}
              setGvFilters={setGvFilters}
              setPage={setPage}
              setRawColModalOpen={setRawColModalOpen}
              setRawRowLimit={setRawRowLimit}
              setSearch={setSearch}
            />
          )}

          {/* Filter modal (reuses the POS filter panel — same field set) */}
          <PosFilterModal
            open={filterModalOpen}
            onClose={() => setFilterModalOpen(false)}
            title="Filter Global View"
            posCount={activeTab === 'pos' ? posCount : filteredKeyData.length}
            filters={gvFilters}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
            currency={gvCurrency}
          />

          {/* Column display modals (new — mirrors gvKeyColModal / gvPosColModal) */}
          <ColumnDisplayModal
            open={keyColModalOpen}
            onClose={() => setKeyColModalOpen(false)}
            columns={KEY_COLUMNS}
            visible={keyColVisibility}
            onApply={setKeyColVisibility}
            title={I18n.t('v2GlobalView.modals.keyColumnDisplayTitle')}
          />
          <ColumnDisplayModal
            open={posColModalOpen}
            onClose={() => setPosColModalOpen(false)}
            columns={GV_POS_COLUMNS}
            visible={posColVisibility}
            onApply={setPosColVisibility}
            title={I18n.t('v2GlobalView.modals.posColumnDisplayTitle')}
          />
          <ColumnDisplayModal
            open={rawColModalOpen}
            onClose={() => setRawColModalOpen(false)}
            columns={rawColumns}
            visible={rawColVisibility}
            onApply={setRawColVisibility}
            title={I18n.t('v2GlobalView.modals.rawColumnDisplayTitle')}
          />

          {/* Save view modal (new — from proto's #gvSaveViewModal) */}
          <Modal
            open={saveViewModalOpen}
            onClose={() => setSaveViewModalOpen(false)}
            title="Save current view"
            subtitle={
              activeFilterCount > 0
                ? `Filters: ${activeFilterCount} applied`
                : 'No filters currently applied.'
            }
            footer={
              <>
                <Button onClick={() => setSaveViewModalOpen(false)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveView}>
                  Save view
                </Button>
              </>
            }
          >
            <SaveViewInput
              type="text"
              placeholder="e.g. Brazil — Active, 1 star"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
            />
          </Modal>

          <PosTrackingDrawer
            actionLists={actionLists}
            activity={activity}
            clearTrackingSelection={clearTrackingSelection}
            onSaveActionList={() => setTrackingActionListModalOpen(true)}
            onWriteEmail={() => setTrackingEmailModalOpen(true)}
            setTrackingDrawerOpen={setTrackingDrawerOpen}
            setTrackingDrawerView={setTrackingDrawerView}
            trackingDrawerOpen={trackingDrawerOpen}
            trackingDrawerView={trackingDrawerView}
            trackingOn={trackingOn}
            trackingSelectedIds={trackingSelectedIds}
          />
          <WriteEmailModal
            open={trackingEmailModalOpen}
            onClose={() => setTrackingEmailModalOpen(false)}
            selectedPos={trackingSelectedPos}
            onSend={() => {
              clearTrackingSelection();
              setTrackingEmailModalOpen(false);
            }}
          />
          <SaveActionListModal
            open={trackingActionListModalOpen}
            onClose={() => setTrackingActionListModalOpen(false)}
            selectedPos={trackingSelectedPos}
            onSave={(data) => {
              createActionList.mutate(
                toCreateActionListBody({
                  name: data.name,
                  action: data.action,
                  notes: data.notes,
                  sentTo: data.sentTo,
                  sentToType: data.sentToType,
                  privacy: data.privacy,
                  storeIds: trackingSelectedPos.map((p) => p.id)
                }),
                {
                  onSuccess: () => {
                    refetchCrmPipeline();
                    clearTrackingSelection();
                    setTrackingActionListModalOpen(false);
                  },
                  onError: (error) => {
                    // eslint-disable-next-line no-console
                    console.error('Failed to save action list:', error);
                  }
                }
              );
            }}
          />
        </>
      )}
    </Page>
  );
};
